import clsx from 'clsx';
import React from 'react';

import {ArtifactVersionMetricChart} from './ArtifactVersionMetricChart';
import {ArtifactVersionMetricEmptyState} from './ArtifactVersionMetricChartPlaceholder';
import {type ArtifactVersionMetricPanelConfig, INITIAL_CONFIG} from './types';
import {useArtifactVersionMetricPlotModel} from './useArtifactVersionMetricPlotModel';

export interface ArtifactVersionPanelMetadata {
  membershipIds: string[];
  totalMemberships: number;
  loading: boolean;
}

export type ArtifactVersionMetricsPanelViewProps = {
  artifactCollectionId?: string;
  artifactVersionMetadata: ArtifactVersionPanelMetadata;
  config: ArtifactVersionMetricPanelConfig;
  onEditPanel?: () => void;
  variant?: 'card' | 'fullscreen';
};

/** Chart content for a grid panel. Card chrome lives in ArtifactVersionMetricPanelCard. */
export const ArtifactVersionMetricsPanelView: React.FC<
  ArtifactVersionMetricsPanelViewProps
> = ({
  artifactCollectionId,
  artifactVersionMetadata,
  config,
  onEditPanel,
  variant = 'card',
}) => {
  const resolvedConfig = {...INITIAL_CONFIG, ...config};
  const membershipIds = resolvedConfig.membershipIds;
  const hasSelection = artifactVersionMetadata.membershipIds.length > 0;

  const plotModel = useArtifactVersionMetricPlotModel({
    artifactCollectionId,
    config: resolvedConfig,
    hasSelection,
    membershipIds,
  });

  // The chart type is chosen via the panel card's configure placeholder, so by
  // the time this view renders a chart type is always set.
  if (resolvedConfig.chartType == null) {
    return null;
  }

  if (plotModel.kind === 'placeholder') {
    return (
      <ArtifactVersionMetricEmptyState
        chartType={resolvedConfig.chartType}
        message={plotModel.message}
        onPress={plotModel.onRetry == null ? onEditPanel : undefined}
        onRetry={plotModel.onRetry}
      />
    );
  }

  return (
    <div
      className="chart flex h-full min-h-0 w-full min-w-0 flex-col p-12"
      data-test="artifact-version-metrics-chart"
      data-test-chart-type={resolvedConfig.chartType}>
      <div
        className={clsx('shrink-0', {
          'h-16': variant === 'fullscreen',
          'h-32': variant !== 'fullscreen',
        })}
        aria-hidden
      />
      {plotModel.title !== '' && (
        <h3
          className={clsx(
            'shrink-0 text-center font-semibold leading-tight text-wb-text-primary dark:text-moon-200',
            {
              'text-lg': variant === 'fullscreen',
              'text-base': variant !== 'fullscreen',
            }
          )}>
          {plotModel.title}
        </h3>
      )}
      {plotModel.partialDataMessage != null && (
        <p className="mt-8 shrink-0 text-xs text-wb-text-tertiary dark:text-moon-400">
          {plotModel.partialDataMessage}
        </p>
      )}
      {/* The shared chart-content class clips react-vis hints with overflow: hidden. */}
      <div className="mt-8 flex min-h-0 max-w-full flex-1">
        <ArtifactVersionMetricChart plot={plotModel} />
      </div>
    </div>
  );
};
